export default customElements.define('w-slider', class Slider extends wealthify.CustomElementBaseClass {
    constructor() {
        const styleSelector = 'c-slider';
        const config = {
            styleSelector,
            templateHTML: `
            <div class="${styleSelector}__container" data-root>
                <label class="${styleSelector}__label" data-label></label>
                <div class="${styleSelector}__slider-container" data-slidercontainer>
                    <input class="u-display__hidden" type="range" data-hiddeninput />
                    <w-widget-button label="Decrease" icon="minus" data-minus></w-widget-button>
                    <div class="${styleSelector}__track-container" aria-hidden="true" data-slider>
                        <div class="${styleSelector}__track"></div>
                        <div class="${styleSelector}__fill" data-fill></div>
                        <div class="${styleSelector}__thumb" data-thumb>
                            <div class="${styleSelector}__thumb-grip">
                                <span class="${styleSelector}__thumb-grip-icon">II</span>
                            </div>
                        </div>
                    </div>
                    <w-widget-button label="Increase" icon="plus" data-plus></w-widget-button>
                </div>
                <span class="${styleSelector}__input u-display__none" data-slot="input" data-input></span>
            </div>`,
            cacheDom: [ 'data-fill', 'data-hiddeninput', 'data-input', 'data-label', 'data-minus', 'data-plus', 'data-slider', 'data-slidercontainer', 'data-thumb' ]
        };

        super(config);

        this.BoundaryTypeEnum = Object.freeze({
            'Lower': 0,
            'Upper': 1
        });

        this.DirectionEnum = Object.freeze({
            'Decrease': 0,
            'Increase': 1
        });

        this.active = false;
        this.clientX;
        this.rightBound;
        this.shiftX;

        this.onConnectedCallback = async () => {
            this.classList.add(this.styleSelector);

            // TODO - Remove after slot refactor
            if (this.#isInputSlotted) {
                this.dom.input.classList.remove('u-display__none');
            }

            this.dom.hiddeninput.addEventListener('valuechange', this._updateSliderPositonFromValue.bind(this));

            this.dom.minus.onclickprop = async () => {
                if (!this.disabledprop){
                    await this._changeInputValue(this.DirectionEnum.Decrease);
                    this.dispatchEvent(new Event('sliderblur'));
                }
            };
            this.dom.plus.onclickprop = async () => {
                if (!this.disabledprop){
                    await this._changeInputValue(this.DirectionEnum.Increase);
                    this.dispatchEvent(new Event('sliderblur'));
                }
            };

            this.dom.slider.addEventListener('mousedown', this._dragStart.bind(this));
            document.addEventListener('mouseup', this._dragEnd.bind(this));
            document.addEventListener('mousemove', this._drag.bind(this));

            // Drag events for mobile
            this.dom.slider.addEventListener('touchstart', this._dragStart.bind(this));
            this.dom.slider.addEventListener('touchend', this._dragEnd.bind(this));
            this.dom.slider.addEventListener('touchmove', this._drag.bind(this));

            // Textbox events
            if (this.dom.input.tagName.toLowerCase() !== 'span' && this.dom.input.tagName.toLowerCase() !== 'w-textbox') {
                wealthify.error.throwError(this.tagName, 'Only w-textbox are allowed to be passed into the data-slot currently');
            }

            this._bindInput();
        };
    }

    // TODO - Remove after slot refactor
    get #isInputSlotted () {
        return this.dom.input !== undefined;
    }

    static get observedProps () {
        return [
            new wealthify.Prop('collapsed', {
                default: false,
                type: wealthify.propTypes.Boolean()
            }),
            wealthify.props.Disabled(),
            new wealthify.Prop('fillbar', {
                default: false,
                type: wealthify.propTypes.Boolean()
            }),
            new wealthify.Prop('valuecontext', {default: 'slider value'}),
            new wealthify.Prop('label', {required: true}),
            new wealthify.Prop('showlabel', {
                type: wealthify.propTypes.Boolean(),
                default: false
            }),
            new wealthify.Prop('min', {type: wealthify.propTypes.Number()}),
            new wealthify.Prop('max', {type: wealthify.propTypes.Number()}),
            new wealthify.Prop('ranges', {
                default: {
                    '0': [0, 1],
                    '100': [100]
                },
                type: wealthify.propTypes.Object()
            }),
            new wealthify.Prop('validoverride', {
                nullable: true,
                type: wealthify.propTypes.Boolean()
            }),
            new wealthify.Prop('value', {
                default: 0,
                type: wealthify.propTypes.Number()
            })
        ];
    }

    get propCallbacks() {
        return {
            collapsed: () => {
                const classListAction = this.collapsed ? 'add' : 'remove';

                this.dom.root.classList[classListAction](`${this.styleSelector}__container--collapsed`);

                if (this.#isInputSlotted) {
                    this.dom.input.classList[classListAction](`${this.styleSelector}__input--collapsed`);
                }
            },

            disabledprop: () => {
                if (this.disabledprop) {
                    this.dom.hiddeninput.setAttribute('disabled', this.disabledprop);
                } else {
                    this.dom.hiddeninput.removeAttribute('disabled');
                }

                if (this.#isInputSlotted) {
                    this.dom.input.disabledprop = this.disabledprop;
                }
            },

            fillbar: (fill) => fill
                ? this.dom.fill.classList.add(`${this.styleSelector}__fill--visible`)
                : this.dom.fill.classList.remove(`${this.styleSelector}__fill--visible`),
            
            valuecontext: () => {
                this.dom.minus.label = 'Decrease ' + this.valuecontext;
                this.dom.plus.label = 'Increase ' + this.valuecontext;
            },

            label: (value) => {
                this.dom.label.textContent = this.label;
                this.dom.hiddeninput.setAttribute('aria-label', value);
            },

            showlabel: () => {
                const classListAction = this.showlabel ? 'remove' : 'add';

                this.dom.label.classList[classListAction]('u-display__none');
                this.dom.hiddeninput.removeAttribute('aria-label', this.label);

            },

            min: (value) => {
                let ranges = this.ranges;

                ranges['0'][0] = value;
                this.ranges = ranges;
                this._updateRanges(ranges);
            },

            max: (value) => {
                let ranges = this.ranges;

                ranges['100'][0] = value;
                this.ranges = ranges;
                this._updateRanges(ranges);
            },

            ranges: (value) => this._updateRanges(value),

            validoverride: () => {
                if (this.#isInputSlotted) {
                    this.dom.input.validoverride = this.validoverride;
                }

                if (this.validoverride == null){
                    this.dom.hiddeninput.setCustomValidity('');
                    this.valid = this.dom.hiddeninput.checkValidity();
                } else {
                    const customValidity = this.validoverride ? '' : 'Invalid field.';

                    this.dom.hiddeninput.setCustomValidity(customValidity);
                    this.valid = this.validoverride;
                }

            },

            value: (value) => {
                if(value != null || value != '' || value != this.value) {
                    this._updateRangeValueFromValue(value);
                    this._updateSliderPositonFromValue();
                
                    this.dispatchEvent(new Event('valuechange'));
                }
            }
        };
    }

    _bindInput () {
        if (this.#isInputSlotted) {
            this.dom.input.value = this.dom.hiddeninput.value;
    
            this.dom.hiddeninput.addEventListener('valuechange', (event) => {
                this.dom.input.value = event.srcElement.value;
                this.valid = this.dom.input.valid;
            });

            this.dom.input.addEventListener('valuechange', (event) => {
                wealthify.request.debounce(this._handleInputUpdateEvent.bind(this, event.srcElement.value))();
            });
        }
    }

    _handleInputUpdateEvent(value) {
        if (value != null && value != '' && value != this.value) {
            this._updateRangeValueFromValue(Number.parseFloat(value));
        }
    }

    _updateRanges(value) {
        if (typeof(value['0']) === 'undefined' || typeof(value['100']) === 'undefined') {
            wealthify.error.throwError(this.tagName, 'The Ranges property must contain a value for 0% and 100%.');
        }

        this.dom.hiddeninput.min = this.ranges['0'][0];
        this.dom.hiddeninput.max = this.ranges['100'][0];
        if (this.#isInputSlotted) {
            this.dom.input.setAttribute('min', this.dom.hiddeninput.min);
            this.dom.input.setAttribute('max', this.dom.hiddeninput.max);
        }
        this.maxRange = this._getMax();

        this._updateRangeValueFromValue(this.value);
        this._updateSliderPositonFromValue();
    }

    _dragStart(event) {
        if (!this.disabledprop){
            event.preventDefault();

            const clientX = this._getClientXPosition(event);

            this.active = true;
            this.shiftX = clientX - this.dom.thumb.getBoundingClientRect().left;
        }
    }
  
    _dragEnd() {
        if (!this.disabledprop){
            if (this.active) {
                this.dispatchEvent(new Event('sliderblur'));
            }

            this.active = false;
        }
    }
  
    _drag(event) {
        if (!this.active || this.disabledprop) {
            return;
        }

        event.preventDefault();
        const clientX = this._getClientXPosition(event);

        let newLeft = clientX - this.shiftX - this.dom.slider.getBoundingClientRect().left;

        this.rightBound = this.dom.slider.offsetWidth - this.dom.thumb.offsetWidth;

        newLeft = newLeft < 0 ? 0 : newLeft;
        newLeft = newLeft > this.rightBound ? this.rightBound : newLeft;

        let barPercentage = (newLeft / this.rightBound) * 100;

        this._updateRangeValueFromSlider(barPercentage);

        this.dom.thumb.style.left = `${(newLeft / this.rightBound) * 100}%`;
        this.dom.fill.style.width = this.dom.thumb.style.left;
    }

    _getClientXPosition(event) {
        if (event.type === 'touchstart' || event.type === 'touchmove') {
            return event.touches[0].clientX;
        } else {
            return event.clientX;
        }
    }

    async _changeInputValue(direction) {
        const stepValue = this._getNextStepValue(direction);

        let canDecreaseValue = (direction === this.DirectionEnum.Decrease && this.value !== Number.parseFloat(this.dom.hiddeninput.min));

        let canIncreaseValue = (direction === this.DirectionEnum.Increase && this.value !== Number.parseFloat(this.dom.hiddeninput.max));
        
        if (canDecreaseValue) {
            this.value -= stepValue;
        } else if (canIncreaseValue) {
            this.value += stepValue;
        }

        this._updateRangeValueFromValue(this.value);
    }

    _updateSliderPositonFromValue() {
        let barValue = this._convertValueToBarValue(this.value);

        this.dom.thumb.style.left = `${barValue}%`;
        this.dom.fill.style.width = `${barValue}%`;
    }

    _updateRangeValueFromSlider(value) {
        this._updateRangeValueFromValue(this._convertBarValueToValue(value));
    }

    _updateRangeValueFromValue(value) {
        if (Number.isNaN(this.value) || this.value == null || this.value < this.dom.hiddeninput.min){
            this.value = this.dom.hiddeninput.min;
        } else if (this.value > this.dom.hiddeninput.max){
            this.value = this.dom.hiddeninput.max;
        } else {
            this.value = value;
        }

        this.dom.hiddeninput.value = this.value;
        this.dom.hiddeninput.dispatchEvent(new Event('valuechange'));
        this._updateButtonDisabledStates();
    }

    _getSortedRangeProperties(isDecendingOrder) {
        let rangeProps = Object.keys(this.ranges).map((range) => Number.parseFloat(range));

        return rangeProps.sort(function (a, b) {
            if (isDecendingOrder) {
                return b - a;
            } else {
                return a - b;
            }
        });
    }

    _getBoundaryByValue(value, boundaryType, isBarValue) {
        const isDescendingOrder = boundaryType === this.BoundaryTypeEnum.Upper;

        const rangeProps = this._getSortedRangeProperties(isDescendingOrder);

        let lastProp;
    
        for (let propIndex in rangeProps) {

            const valueLessThanRangeValue =
                (isBarValue && rangeProps[propIndex] < value) ||
                (!isBarValue && this.ranges[rangeProps[propIndex]][0] < value);

            const valueGreaterThanOrEqualToRangeValue =
                (isBarValue && rangeProps[propIndex] >= value) ||
                (!isBarValue && this.ranges[rangeProps[propIndex]][0] >= value);

            if (!lastProp) {
                lastProp = rangeProps[propIndex];
            }
    
            if (boundaryType === this.BoundaryTypeEnum.Upper) {
                if (valueLessThanRangeValue) {
                    break;
                }
            } else if (boundaryType === this.BoundaryTypeEnum.Lower) {
                if (rangeProps[propIndex] === rangeProps[rangeProps.length - 1]) {
                    lastProp = rangeProps[rangeProps.length - 2];
                    break;
                } else if (valueGreaterThanOrEqualToRangeValue) {
                    break;
                }
            }
            lastProp = rangeProps[propIndex];
        }
        
        return {
            value: lastProp,
            lowerBoundary: this.ranges[lastProp][0],
            stepSize: this.ranges[lastProp][1]
        };
    }
    
    _getMax() {
        const rangeProps = this._getSortedRangeProperties(this.BoundaryTypeEnum.Upper);
    
        return {
            barValue: rangeProps[0],
            lowerBoundary: this.ranges[rangeProps[0]][0],
            stepSize: this.ranges[rangeProps[0]][1]
        };
    }

    _getNextStepValue(direction) {
        const upper = this._getBoundaryByValue(this.value, this.BoundaryTypeEnum.Upper, false);

        const lower = this._getBoundaryByValue(this.value, this.BoundaryTypeEnum.Lower, false);

        let stepSize;

        if (this.value === upper.lowerBoundary) {
            direction === this.DirectionEnum.Increase  ? stepSize = upper.stepSize : stepSize = lower.stepSize;
        } else {
            stepSize = lower.stepSize;
        }

        return stepSize;
    }
    
    _convertValueToBarValue(value) {    
        if (value < this.maxRange.lowerBoundary) {
            const upper = this._getBoundaryByValue(value, this.BoundaryTypeEnum.Upper, false);
            const lower = this._getBoundaryByValue(value, this.BoundaryTypeEnum.Lower, false);
    
            const barBoundaryRange = upper.value - lower.value;
    
            const valueRange = upper.lowerBoundary - lower.lowerBoundary;
            const differenceAboveLowerBoundary = value - lower.lowerBoundary;
    
            const proportionOfValueRange = differenceAboveLowerBoundary / valueRange;
    
            if (proportionOfValueRange > 0) {
                return (proportionOfValueRange * barBoundaryRange) + lower.value;
            } else {
                return lower.value;
            }
        }

        return this.maxRange.barValue;
    }
    
    _convertBarValueToValue(barValue) {
        if (barValue < this.maxRange.barValue) {
            const upper = this._getBoundaryByValue(barValue, this.BoundaryTypeEnum.Upper, true);
            const lower = this._getBoundaryByValue(barValue, this.BoundaryTypeEnum.Lower, true);
    
            const differenceAboveLowerBoundary = barValue - lower.value;
    
            const barValueRange = upper.value - lower.value;
    
            const valueRange = upper.lowerBoundary - lower.lowerBoundary;
    
            if (differenceAboveLowerBoundary > 0) {
                const exactAmount = ((differenceAboveLowerBoundary / barValueRange) * valueRange) + lower.lowerBoundary;
                const extra = (exactAmount % lower.stepSize);
                const steps = (exactAmount - extra) / lower.stepSize;
    
                if (extra > 0) {
                    return (steps + 1) * lower.stepSize;
                } else {
                    return exactAmount;
                }
            } else {
                return lower.lowerBoundary;
            }
        }
        
        return this.maxRange.lowerBoundary;
    }

    _updateButtonDisabledStates () {
        const rangeKeys = Object.keys(this.ranges);
        const isLowest = this.value.toString() === rangeKeys[0];
        const isHighest = this.value.toString() === rangeKeys[rangeKeys.length - 1];

        this.dom.minus.disabledprop = isLowest ? true : false;
        this.dom.plus.disabledprop = isHighest ? true : false;
    }
});